import React, {
    FC, useState,
} from 'react';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import OtpInput from 'react-otp-input';
import classNames from 'classnames';
import { StatusMessage } from '@/components/Common/CodeAuthenticator';
import styles from './OTPAuthCodeBox.module.scss';
import Timer from '@/components/Common/Timer';
import i18n from '@/content';
import ColoredTooltip from '@/components/Common/ColoredTooltip';
import { useStores } from '@/components/hooks';

const RESEND_TIMEOUT_DEFAULT = 60;

type CodeSendingMethod = 'phone' | 'voice';

interface OTPAuthCodeBoxProps {
    recipientEmail: string;
    onCodeSubmit: (code: string) => void;
    onResendCode: (method: CodeSendingMethod) => void;
    onEditEmail?: () => void;
    isPhoneVerification?: boolean;
    errorMessages?: StatusMessage[];
    hasEmailVerification?: boolean;
    codeError: boolean;
    setCodeError: (value: boolean) => void;
}

const OTPAuthCodeBox: FC<OTPAuthCodeBoxProps> = observer(({
    recipientEmail,
    onCodeSubmit,
    onResendCode,
    onEditEmail,
    isPhoneVerification = false,
    codeError = false,
    setCodeError,
}) => {
    const { t } = useTranslation();
    const nameSpace = 'recipientPage';
    const [codeValue, setCodeValue] = React.useState('');
    const [showCodeTimeout, setShowCodeTimeout] = useState<boolean>(false);
    const [showVoiceTimeout, setShowVoiceTimeout] = useState<boolean>(false);
    const [resendRestricted, setResendRestricted] = useState<boolean>(false);
    const [recallRestricted, setRecallRestricted] = useState<boolean>(false);
    const [wrongCodeAttempts, setWrongCodeAttempts] = useState<number>(2);

    const { recipientPageStore: { sharedFilesStore: { isContentLoading } } } = useStores();

    const handleCodeChange = (value: string) => {
        setCodeValue(value);
        if (value.length === 6) {
            onCodeSubmit(value);
            if (codeError) {
                setWrongCodeAttempts(wrongCodeAttempts - 1);
            } else {
                setWrongCodeAttempts(2);
            }
        }
    };

    const enableCallButton = (): void => {
        setShowVoiceTimeout(false);
        setRecallRestricted(false);
    };

    const resendCall = (): void => {
        onResendCode('voice');
        setShowVoiceTimeout(true);
        setRecallRestricted(true);
        setWrongCodeAttempts(2);
        setCodeValue('');
        setCodeError(false);
    };

    const enableResendButton = (): void => {
        setShowCodeTimeout(false);
        setResendRestricted(false);
    };

    const resendCode = (): void => {
        onResendCode('phone');
        setShowCodeTimeout(true);
        setResendRestricted(true);
        setWrongCodeAttempts(2);
        setCodeValue('');
        setCodeError(false);
    };

    return (
        <div className={styles.codeBoxContainer} data-testid="recipientPage_auth_codeBoxContainer">
            <h2 className={styles.title} data-testid="recipientPage_auth_codeTitle">
                {t(`${nameSpace}.OTPSteps.check${isPhoneVerification ? 'Phone' : 'Email'}`)}
            </h2>
            <div className={styles.description} data-testid="recipientPage_auth_codeDescription">
                <Trans
                    i18nKey={`${nameSpace}.OTPSteps.enterCode`}
                    t={t}
                    tOptions={{ recipientEmail }}
                >
                    {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                    Enter the 6-digit code below, which we’ve sent to <strong className={styles.email}>recipientEmail</strong>
                </Trans>
                <span className={styles.iconWrapper} data-testid="recipientPage_auth_editButton">
                    {isPhoneVerification ? (
                        <ColoredTooltip title={i18n.t(`${nameSpace}.OTPSteps.tooltip`)} placement="topLeft">
                            <InfoCircleOutlined style={{ fontSize: '16px' }} />
                        </ColoredTooltip>
                    ) : (
                        <EditOutlined
                            style={{ fontSize: '16px', cursor: 'pointer' }}
                            onClick={onEditEmail}
                        />
                    )}
                </span>
            </div>
            <div className={styles.codeInputsWrapper} data-testid="recipientPage_auth_codeInputsWrapper">
                <OtpInput
                    containerStyle={styles['OTP-input']}
                    inputStyle={classNames(styles['code-input-box'], { [styles['error-otp']]: codeError })}
                    value={codeValue}
                    onChange={handleCodeChange}
                    numInputs={6}
                    inputType="tel"
                    shouldAutoFocus
                    renderInput={(props) => <input {...props} disabled={!wrongCodeAttempts} data-testid="recipientPage_auth_codeInput" />}
                />
            </div>
            {codeError && !isContentLoading
                && (
                    <div className={styles['code-error-message']} data-testid="recipientPage_auth_wrongCodeAttempts">
                        {i18n.t(`${nameSpace}.OTPSteps.wrongCode`, { attemptsRemain: wrongCodeAttempts })}
                    </div>
                )}
            {codeError && !wrongCodeAttempts && !isContentLoading
                && (
                    <div className={styles['code-error-message']} data-testid="recipientPage_auth_resendAndTryAgain">
                        {i18n.t(`${nameSpace}.OTPSteps.resendAndTryAgain`)}
                    </div>
                )}
            <div className={styles.resendWrapper}>
                <span className={styles.resendText}>
                    {t(`${nameSpace}.OTPSteps.noCode`)}
                    {' '}
                </span>
                {showCodeTimeout ? (
                    <Timer
                        className={classNames(styles.timer, styles[i18n.dir()])}
                        countdownSeconds={RESEND_TIMEOUT_DEFAULT}
                        onTimeout={enableResendButton}
                        prefix={i18n.t(`${nameSpace}.OTPSteps.sendNewEmailCodeIn`)}
                    />
                ) : (
                    <button
                        className={styles.resendButton}
                        onClick={resendCode}
                        disabled={resendRestricted}
                    >
                        {t(`${nameSpace}.OTPSteps.sendNewCode`)}
                    </button>
                )}
            </div>
            {isPhoneVerification
                && (
                    <div className={styles.resendCallWrapper}>
                        <span className={styles.resendText}>
                            {t(`${nameSpace}.OTPSteps.preferVoice`)}
                        </span>
                        {
                            showVoiceTimeout ? (
                                <Timer
                                    className={classNames(styles.timer, styles[i18n.dir()])}
                                    countdownSeconds={RESEND_TIMEOUT_DEFAULT}
                                    onTimeout={enableCallButton}
                                    prefix={i18n.t(`${nameSpace}.OTPSteps.callAgain`)}
                                />
                            ) : (
                                <button
                                    className={styles.resendButton}
                                    onClick={resendCall}
                                    disabled={recallRestricted}
                                >
                                    {t(`${nameSpace}.OTPSteps.sendVoiceCode`)}
                                </button>
                            )
                        }

                    </div>
                )}
        </div>
    );
});

export default OTPAuthCodeBox;
